<script setup lang="ts">
import { ref, computed } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { apiClient } from '@/shared/api/client';
import OfficerCessationForm from './OfficerCessationForm.vue';

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

// Props
interface Props {
  taskData?: any;
  taskId?: string;
  showBackButton?: boolean;
  readOnly?: boolean; // 읽기 전용 모드 (E-Filing 완료 상태)
}

const props = withDefaults(defineProps<Props>(), {
  taskData: null,
  taskId: '',
  showBackButton: true,
  readOnly: false
});

// Emits
const emit = defineEmits<{
  back: [];
  submit: [data: any];
  error: [error: any];
  'validation-success': [data: { eFilingData: any; entityUEN: string; filingAgentUEN: string; entityName: string }];
}>();

// OfficerCessationForm ref
const formRef = ref<InstanceType<typeof OfficerCessationForm> | null>(null);

// 상태
const isSubmitting = ref(false);
const isValidating = ref(false);

// 디버깅용 E-Filing 데이터
const debugData = ref<{
  originalEFilingData: any;
  mergedEFilingData: any;
  changes: Array<{ path: string; oldValue: any; newValue: any }>;
}>({
  originalEFilingData: null,
  mergedEFilingData: null,
  changes: []
});

// 검증 결과
const validationResult = ref<any>(null);

// 필드명을 사용자 친화적으로 변환
const formatFieldLabel = (field: any): string => {
  let label = field.label || field.field || '';
  if (label.endsWith(' Code') || label.endsWith(' Name')) {
    label = label.replace(/ Code$/, '').replace(/ Name$/, '');
  }
  return label;
};

// 필드별 validation 에러 매핑 (OfficerCessationForm에 전달)
const fieldValidationErrors = computed(() => {
  const fields = validationResult.value?.data?.missingFields || [];
  if (!fields.length) return {};

  const errors: Record<string, string> = {};
  
  fields.forEach((field: any) => {
    const fieldPath = field.field || '';
    const label = formatFieldLabel(field);
    
    // 필드 경로를 컴포넌트의 필드명으로 매핑
    const mappedField = mapFieldPathToComponentField(fieldPath);
    if (mappedField) {
      errors[mappedField] = t('startTask.This field is required') || `${label} is required`;
    }
  });

  return errors;
});

// 백엔드 필드 경로를 컴포넌트 필드명으로 매핑 (Cessation용)
const mapFieldPathToComponentField = (fieldPath: string): string | null => {
  // Cessation 필드 경로 예시: "positionHeld", "ceasedDate", "ceasedReason", "entityType.code", "entityType.name" 등
  const fieldMap: Record<string, string> = {
    'positionHeld': 'position',
    'ceasedDate': 'dateOfCessation',
    'ceasedReason': 'reasonForCessation',
    'ceasedReasonOther': 'otherReason',
    'entityType.code': 'entityType',
    'entityType.name': 'entityType',
    'entityUEN': 'entityUEN',
    'filingAgentUEN': 'filingAgentUEN',
    // WithdrawPositionHolder 관련 필드
    'withdrawPositionHolder.registrationIdNo': 'registrationIdNo',
    'withdrawPositionHolder.officerCategory.code': 'officerCategory',
    'withdrawPositionHolder.officerCategory.name': 'officerCategory',
    'withdrawPositionHolder.profileId': 'profileId',
    'withdrawPositionHolder.name': 'officerName',
  };

  // 정확한 매칭 먼저 시도
  if (fieldMap[fieldPath]) {
    return fieldMap[fieldPath];
  }

  // 점(.)으로 구분된 경로 처리 (예: "entityType.code" -> "entityType")
  const pathParts = fieldPath.split('.');
  if (pathParts.length > 1) {
    const baseField = pathParts[0];
    if (fieldMap[fieldPath]) {
      return fieldMap[fieldPath];
    }
    // baseField만 매핑 시도
    if (fieldMap[baseField]) {
      return fieldMap[baseField];
    }
  }

  // 매핑되지 않은 필드는 null 반환
  return null;
};

// 최종 전송용 데이터 (변환된 eFilingData)
const finalEFilingData = ref<any>(null);

// Singpass QR 모달은 EFilingPage에서 관리하므로 제거

// Entity 정보 (taskData 또는 finalEFilingData에서 추출)
const entityUEN = computed(() => {
  const fromEFilingData = props.taskData?.eFilingData?.entityUEN;
  const fromTaskData = props.taskData?.entityUEN;
  const fromFinalData = finalEFilingData.value?.entityUEN;
  // WithdrawList의 첫 번째 항목에서 EntityUEN 추출
  const fromWithdrawList = props.taskData?.eFilingData?.withdrawList?.[0]?.EntityUEN ||
                          props.taskData?.eFilingData?.WithdrawList?.[0]?.EntityUEN ||
                          finalEFilingData.value?.withdrawList?.[0]?.EntityUEN;
  // EntityUEN 대문자 키로도 확인
  const fromEntityType = props.taskData?.eFilingData?.EntityUEN ||
                         finalEFilingData.value?.EntityUEN;

  const result = fromEFilingData || fromTaskData || fromFinalData || fromWithdrawList || fromEntityType || '';
  console.log('📋 [OfficerCessationEFiling] entityUEN computed:', {
    fromEFilingData,
    fromTaskData,
    fromFinalData,
    fromWithdrawList,
    fromEntityType,
    result,
    taskDataKeys: Object.keys(props.taskData || {}),
    eFilingDataKeys: Object.keys(props.taskData?.eFilingData || {})
  });
  return result;
});

const filingAgentUEN = computed(() => {
  const fromEFilingData = props.taskData?.eFilingData?.filingAgentUEN;
  const fromTaskData = props.taskData?.filingAgentUEN;
  const fromFinalData = finalEFilingData.value?.filingAgentUEN;
  // FilingAgentUEN 대문자 키로도 확인
  const fromUpperCase = props.taskData?.eFilingData?.FilingAgentUEN ||
                        finalEFilingData.value?.FilingAgentUEN;

  return fromEFilingData || fromTaskData || fromFinalData || fromUpperCase || entityUEN.value;
});

const entityName = computed(() => {
  return props.taskData?.eFilingData?.entityName?.name ||
         props.taskData?.eFilingData?.entityName ||
         props.taskData?.entityName ||
         finalEFilingData.value?.entityName ||
         '';
});

// { code, name } 객체를 name 값으로 변환하는 함수
const transformCodeNameToValue = (data: any): any => {
  if (data === null || data === undefined) {
    return data;
  }

  // 배열인 경우 각 요소를 재귀적으로 변환
  if (Array.isArray(data)) {
    return data.map(item => transformCodeNameToValue(item));
  }

  // 객체인 경우
  if (typeof data === 'object') {
    // { code, name } 형태인지 확인
    const keys = Object.keys(data);
    if (keys.length === 2 && keys.includes('code') && keys.includes('name')) {
      // name 값 반환 (name이 비어있으면 code 반환)
      return data.name || data.code || '';
    }

    // 일반 객체는 각 속성을 재귀적으로 변환
    const result: any = {};
    for (const key of keys) {
      result[key] = transformCodeNameToValue(data[key]);
    }
    return result;
  }

  // 기본형은 그대로 반환
  return data;
};

// 뒤로가기
const handleBack = () => {
  emit('back');
};

// Deep merge 함수 - 빈 값은 기존 값 유지
const deepMerge = (base: any, update: any): any => {
  if (!base) return update;
  if (!update) return base;

  // 둘 다 객체가 아니면 update 값 반환 (단, 빈 값이면 base 유지)
  if (typeof base !== 'object' || typeof update !== 'object') {
    // update가 빈 문자열이나 null/undefined면 base 유지
    if (update === '' || update === null || update === undefined) {
      return base;
    }
    return update;
  }

  // 배열인 경우
  if (Array.isArray(base) && Array.isArray(update)) {
    // 배열 요소별 deep merge
    const maxLen = Math.max(base.length, update.length);
    const result = [];
    for (let i = 0; i < maxLen; i++) {
      if (i < update.length && i < base.length) {
        result.push(deepMerge(base[i], update[i]));
      } else if (i < base.length) {
        result.push(base[i]);
      } else {
        result.push(update[i]);
      }
    }
    return result;
  }

  // 객체인 경우 - 모든 키에 대해 deep merge
  const result = { ...base };
  for (const key of Object.keys(update)) {
    const updateValue = update[key];
    const baseValue = base[key];

    // code/name 객체인 경우 특별 처리
    if (typeof updateValue === 'object' && updateValue !== null &&
        'code' in updateValue && 'name' in updateValue) {
      // code나 name이 비어있으면 base 값 유지
      if (!updateValue.code && !updateValue.name) {
        result[key] = baseValue || updateValue;
      } else if (!updateValue.code && baseValue?.code) {
        result[key] = { ...updateValue, code: baseValue.code };
      } else if (!updateValue.name && baseValue?.name) {
        result[key] = { ...updateValue, name: baseValue.name };
      } else {
        result[key] = deepMerge(baseValue, updateValue);
      }
    } else {
      result[key] = deepMerge(baseValue, updateValue);
    }
  }

  return result;
};

// task_data 기반으로 eFilingData 업데이트 (deep merge)
const mergeTaskDataWithEFiling = (baseTaskData: any, newEFilingData: any): any => {
  // 기본 task_data 복사
  const merged = JSON.parse(JSON.stringify(baseTaskData || {}));

  // 기존 eFilingData가 있으면 deep merge, 없으면 새로운 eFilingData 사용
  if (merged.eFilingData) {
    merged.eFilingData = deepMerge(merged.eFilingData, newEFilingData);
  } else {
    merged.eFilingData = newEFilingData;
  }

  return merged;
};

// Submit - E-Filing 데이터 생성 후 검증 및 저장
const handleSubmit = async () => {
  console.log('📋 [OfficerCessationEFiling] Submit 클릭');

  if (isSubmitting.value) return;

  try {
    isSubmitting.value = true;

    if (!formRef.value) {
      console.error('❌ [OfficerCessationEFiling] Form ref가 없습니다');
      return;
    }

    // OfficerCessationForm의 getEFilingData 호출
    const updatedEFilingData = await formRef.value.getEFilingData?.();
    console.log('📋 [OfficerCessationEFiling] Updated E-Filing 데이터:', updatedEFilingData);

    // task_data 기반으로 eFilingData 업데이트
    const mergedData = mergeTaskDataWithEFiling(props.taskData, updatedEFilingData);
    console.log('📋 [OfficerCessationEFiling] Merged 데이터:', mergedData);

    // 원본 eFilingData와 병합된 eFilingData
    const originalEFilingData = props.taskData?.eFilingData || null;
    const mergedEFilingData = mergedData.eFilingData || null;

    // 디버깅용 다이얼로그 데이터 저장 (검증에 필요)
    debugData.value = {
      originalEFilingData,
      mergedEFilingData,
      changes: []
    };
    
    // 바로 검증 및 저장 실행
    await handleValidateAndSubmit();
  } finally {
    isSubmitting.value = false;
  }
};

// 검증 및 저장
const handleValidateAndSubmit = async () => {
  console.log('📋 [OfficerCessationEFiling] Save & Validate 호출');

  if (isValidating.value) return;

  try {
    isValidating.value = true;

    if (!props.taskId) {
      console.error('❌ [OfficerCessationEFiling] taskId가 없습니다');
      validationResult.value = {
        success: false,
        error: 'Task ID is required'
      };
      return;
    }

    // 1-a. Change FYE와 동일하게 기존 task_id 기준 UPDATE (INSERT 없음)
    //      - TASK_DATA.eFilingData 갱신
    //      - L_TASK_EFILING_DATA 최신 row의 REQUEST_PAYLOAD 갱신
    await apiClient.put(
      `/startTask/officer-cessation/update-form-data/${props.taskId}`,
      { eFilingDataForApi: debugData.value.mergedEFilingData }
    );
    console.log('✅ [OfficerCessationEFiling] REQUEST_PAYLOAD 업데이트 완료 (L_TASK_EFILING_DATA)');

    // 1-b. 기존 경로 유지: validate API 호환을 위해 TASK_DATA.eFilingData도 동기화
    console.log('📤 [OfficerCessationEFiling] eFilingData 저장 중...');
    const saveResponse = await apiClient.put(
      `/startTask/officer-cessation/update-efiling-data/${props.taskId}`,
      { eFilingData: debugData.value.mergedEFilingData }
    );
    console.log('✅ [OfficerCessationEFiling] eFilingData 저장 완료:', saveResponse);

    // 2. /validate/:taskId API 호출
    console.log('📤 [OfficerCessationEFiling] Validate 호출 중...');
    const response = await apiClient.get(`/startTask/officer-cessation/validate/${props.taskId}`) as any;
    console.log('✅ [OfficerCessationEFiling] Validate 응답:', response);
    console.log('✅ [OfficerCessationEFiling] Validate 응답 구조:', {
      response: response,
      responseData: response?.data,
      responseDataData: response?.data?.data,
      isValid: response?.data?.isValid || response?.data?.data?.isValid || response?.isValid,
      missingFields: response?.data?.missingFields || response?.data?.data?.missingFields || response?.missingFields || []
    });

    validationResult.value = {
      success: true,
      data: response?.data?.data || response?.data || response
    };

    // isValid 값 확인 (여러 경로에서 확인)
    const isValid = validationResult.value?.data?.isValid ?? 
                   response?.data?.isValid ?? 
                   response?.data?.data?.isValid ?? 
                   response?.isValid ?? 
                   false;
    
    console.log('🔍 [OfficerCessationEFiling] 검증 결과:', {
      isValid: isValid,
      missingFieldsCount: validationResult.value?.data?.missingFields?.length || 0,
      missingFields: validationResult.value?.data?.missingFields || []
    });

    // 검증 성공 시 최종 전송용 데이터 생성 (code/name -> name으로 변환)
    if (isValid) {
      const transformedData = transformCodeNameToValue(debugData.value.mergedEFilingData);
      finalEFilingData.value = transformedData;
      console.log('📋 [OfficerCessationEFiling] 변환된 최종 데이터:', finalEFilingData.value);
      
      // Singpass 모달에 전달할 데이터 로그 출력
      const singpassModalProps = {
        isModal: true,
        filingAgentUEN: filingAgentUEN.value,
        entityUEN: entityUEN.value,
        entityName: entityName.value,
        callCheckCSP: false,
        callGenFullSyncTask: false,
        callGetEntityData: true,
        eFilingData: finalEFilingData.value,
        apiName: 'saveDraftOfficer',
        eFilingApiPath: '/startTask/officer-cessation/e-filing/submit',
        taskId: props.taskId
      };
      console.log('🔐 [OfficerCessationEFiling] ========================================');
      console.log('🔐 [OfficerCessationEFiling] Singpass 모달 열기 - 전달할 Props:');
      console.log('🔐 [OfficerCessationEFiling] ========================================');
      console.log('🔐 [OfficerCessationEFiling] Props:', {
        isModal: singpassModalProps.isModal,
        filingAgentUEN: singpassModalProps.filingAgentUEN,
        entityUEN: singpassModalProps.entityUEN,
        entityName: singpassModalProps.entityName,
        callCheckCSP: singpassModalProps.callCheckCSP,
        callGenFullSyncTask: singpassModalProps.callGenFullSyncTask,
        callGetEntityData: singpassModalProps.callGetEntityData,
        apiName: singpassModalProps.apiName,
        eFilingApiPath: singpassModalProps.eFilingApiPath,
        taskId: singpassModalProps.taskId
      });
      console.log('🔐 [OfficerCessationEFiling] eFilingData 구조:', {
        hasWithdrawList: !!finalEFilingData.value?.WithdrawList,
        withdrawListLength: finalEFilingData.value?.WithdrawList?.length || 0,
        EntityUEN: finalEFilingData.value?.EntityUEN || 'N/A',
        FilingAgentUEN: finalEFilingData.value?.FilingAgentUEN || 'N/A'
      });
      console.log('🔐 [OfficerCessationEFiling] 전체 eFilingData (JSON):', JSON.stringify(finalEFilingData.value, null, 2));
      console.log('🔐 [OfficerCessationEFiling] ========================================');
      
      // 검증 성공 시 부모 컴포넌트(EFilingPage)로 데이터 전달하여 Singpass 모달 호출 (OfficerAppointmentEFiling과 동일)
      console.log('✅ [OfficerCessationEFiling] 검증 성공 - 부모(EFilingPage)에 validation-success 이벤트 전달');
      emit('validation-success', {
        eFilingData: finalEFilingData.value,
        entityUEN: entityUEN.value,
        filingAgentUEN: filingAgentUEN.value,
        entityName: entityName.value
      });
    } else {
      // 검증 실패 시 로그 출력
      console.error('❌ [OfficerCessationEFiling] ========================================');
      console.error('❌ [OfficerCessationEFiling] 검증 실패 - Singpass 모달을 열 수 없습니다');
      console.error('❌ [OfficerCessationEFiling] ========================================');
      console.error('❌ [OfficerCessationEFiling] 누락된 필드:', validationResult.value?.data?.missingFields || []);
      console.error('❌ [OfficerCessationEFiling] isValid 값:', isValid);
      console.error('❌ [OfficerCessationEFiling] validationResult:', validationResult.value);
    }
  } catch (error: any) {
    console.error('❌ [OfficerCessationEFiling] Save & Validate 실패:', error);
    validationResult.value = {
      success: false,
      error: error.message || 'Save or Validation failed'
    };
  } finally {
    isValidating.value = false;
  }
};

// Singpass 관련 핸들러는 EFilingPage.vue로 이동됨 (OfficerAppointmentEFiling과 동일)

// Form ref를 외부에서 접근 가능하도록 expose
defineExpose({
  formRef,
  getEFilingData: async () => {
    if (formRef.value) {
      return await formRef.value.getEFilingData?.();
    }
    return null;
  },
  getMergedData: async () => {
    if (formRef.value) {
      const updatedEFilingData = await formRef.value.getEFilingData?.();
      return mergeTaskDataWithEFiling(props.taskData, updatedEFilingData);
    }
    return null;
  }
});
</script>

<template>
  <v-card rounded="md" class="officer-cessation-efiling">
    <v-card-text>
      <OfficerCessationForm
        ref="formRef"
        :task-config="taskData?.taskConfig"
        :initial-data="taskData"
        :read-only="readOnly"
        :show-task-flow="false"
        :e-filing-mode="true"
        :validation-errors="fieldValidationErrors"
      />
    </v-card-text>
    <v-divider v-if="!readOnly" />
    <v-card-actions v-if="!readOnly" class="justify-end pa-4">
      <v-btn
        v-if="showBackButton"
        variant="outlined"
        color="primary"
        :disabled="isSubmitting || isValidating"
        @click="handleBack"
      >
        {{ t('startTask.Back') || 'Back' }}
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :loading="isSubmitting || isValidating"
        :disabled="isSubmitting || isValidating"
        @click="handleSubmit"
      >
        {{ t('startTask.Submit') || 'Submit' }}
      </v-btn>
    </v-card-actions>

    <!-- 읽기 전용 모드일 때 완료 상태 표시 -->
    <v-divider v-if="readOnly" />
    <v-card-actions v-if="readOnly" class="justify-center pa-4">
      <v-alert type="success" variant="tonal" density="compact" class="mb-0">
        {{ t('startTask.E-Filing has been completed') || 'E-Filing has been completed' }}
      </v-alert>
    </v-card-actions>
  </v-card>

  <!-- Singpass QR 인증 모달은 EFilingPage.vue로 이동됨 (OfficerAppointmentEFiling과 동일) -->
</template>

<style scoped>
.officer-cessation-efiling {
  margin: 16px;
  max-width: 100%;
  box-sizing: border-box;
}

.officer-cessation-efiling :deep(.v-card-text) {
  max-width: 100%;
  box-sizing: border-box;
  padding: 24px;
}

/* v-row의 음수 마진으로 인한 가로 스크롤 방지 */
.officer-cessation-efiling :deep(.v-row) {
  margin-left: 0;
  margin-right: 0;
}

.officer-cessation-efiling :deep(.v-col) {
  padding-left: 12px;
  padding-right: 12px;
}

/* 카드 액션 버튼 간격 및 우측 여백 */
.officer-cessation-efiling :deep(.v-card-actions) {
  gap: 8px;
  padding-right: 32px !important; /* pr-8 = 32px */
}

/* 반응형 조절 - 작은 화면에서 패딩 감소 */
@media (max-width: 960px) {
  .officer-cessation-efiling :deep(.v-card-text) {
    padding: 16px;
  }
  
  .officer-cessation-efiling :deep(.v-col) {
    padding-left: 8px;
    padding-right: 8px;
  }
}
</style>

